import { GenerateSignaturePacketPageLegalEntityBankAccountsAndContactsQuery } from '@/__generated__/graphql';
import { AddressComponent } from './rowComponents/addressComponent';
import { AttachmentsComponent } from './rowComponents/attachmentsComponent';
import { BankAccountsComponent } from './rowComponents/bankAccountsComponent';
import { CheckboxGroupComponent } from './rowComponents/checkboxGroupComponent';
import { ContactsComponent } from './rowComponents/contactsComponent';
import { CustomComponent } from './rowComponents/customComponents';
import { HeaderComponent } from './rowComponents/headerComponent';
import { HtmlComponent } from './rowComponents/htmlComponent';
import { InfoBlockComponent } from './rowComponents/infoBlockComponent';
import { RadioComponent } from './rowComponents/radioComponent';
import { TextareaComponent } from './rowComponents/textareaComponent';
import { IRenderTemplatePage, IRenderTemplateRow, OpenWizardModalPageArgsType } from './types';

interface RowComponentProps {
  row: IRenderTemplateRow;
  mode: 'documentationTab' | 'wizard';
  pageName?: string;
  pageData?: IRenderTemplatePage;
  contacts?: GenerateSignaturePacketPageLegalEntityBankAccountsAndContactsQuery['legalEntity']['contacts'];
  bankAccounts?: GenerateSignaturePacketPageLegalEntityBankAccountsAndContactsQuery['legalEntity']['bankAccounts'];
  entityId?: string;
  templateId?: string;
  ignoreRequired?: boolean;
  documentationTabFormData?: Record<string, any>;
  deleteItem?: (type: 'bankAccounts' | 'contacts', id: string) => void;
  openModalPage: ({ type, row, id, currentAttachment, sectionName, expectedAttachmentName, contactId }: OpenWizardModalPageArgsType) => void;
}

export const RowComponent = ({
  row,
  mode,
  pageName = '',
  pageData,
  contacts = [],
  bankAccounts = [],
  entityId = '',
  templateId = '',
  ignoreRequired = false,
  documentationTabFormData = {},
  deleteItem = () => {},
  openModalPage
}: RowComponentProps) => {
  switch (row.component) {
    case 'address':
      return <AddressComponent row={row} pageName={pageName} ignoreRequired={ignoreRequired} />;
    case 'attachments':
      return (
        <AttachmentsComponent
          row={row}
          mode={mode}
          pageName={pageName}
          entityId={entityId}
          templateId={templateId}
          ignoreRequired={ignoreRequired}
          contacts={contacts}
          documentationTabFormData={documentationTabFormData}
          openModalPage={openModalPage}
        />
      );
    case 'bankAccounts':
      return (
        <BankAccountsComponent
          row={row}
          mode={mode}
          pageName={pageName}
          bankAccounts={bankAccounts}
          ignoreRequired={ignoreRequired}
          deleteItem={deleteItem}
          openModalPage={openModalPage}
        />
      );
    case 'checkboxGroup':
      return <CheckboxGroupComponent row={row} pageName={pageName} pageData={pageData} ignoreRequired={ignoreRequired} />;
    case 'contacts':
      // Compute sibling contact fields with the same group
      const currentGroup = row.config?.group;
      const siblingContactFieldNames = currentGroup
        ? pageData?.rows
            ?.filter((r) => r.component === 'contacts' && r.config?.group === currentGroup && r.name !== row.name)
            .map((r) => r.name) ?? []
        : [];

      return (
        <ContactsComponent
          row={row}
          mode={mode}
          pageName={pageName}
          entityId={entityId}
          contacts={contacts}
          ignoreRequired={ignoreRequired}
          deleteItem={deleteItem}
          openModalPage={openModalPage}
          siblingContactFieldNames={siblingContactFieldNames}
        />
      );
    case 'custom':
      return <CustomComponent row={row} pageName={pageName} ignoreRequired={ignoreRequired} />;
    case 'header':
      return <HeaderComponent row={row} pageName={pageName} />;
    case 'html':
      return <HtmlComponent row={row} pageName={pageName} htmlString={row?.config?.content || ''} />;
    case 'infoBlock':
      return <InfoBlockComponent row={row} pageName={pageName} />;
    case 'radio':
      return <RadioComponent row={row} pageName={pageName} ignoreRequired={ignoreRequired} />;
    case 'textarea':
      return <TextareaComponent row={row} pageName={pageName} ignoreRequired={ignoreRequired} />;
    default:
      return <></>;
  }
};
